import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Send, ArrowUpRight, Copy, Check } from "lucide-react";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Television Booking", "Podcast / Radio Interview", "Keynote / Speaking", "Print / Digital Press", "General Inquiry"];

const inputCls =
  "w-full border-b border-[#6B5D4F]/25 bg-transparent py-3.5 font-body text-base font-light text-[#6B5D4F] placeholder:text-[#8B7D6B]/50 focus:border-[#C5A059] focus:outline-none transition-colors duration-300";

const BIOS = [
  {
    label: "Short Bio",
    text: "Susan Grau is an international bestselling author, inspirational speaker, grief and trauma expert, and intuitive medium whose work has reached over 580 million people worldwide.",
  },
  {
    label: "Standard Bio",
    text: "Susan Grau is an international bestselling author, inspirational speaker, grief and trauma expert, and intuitive medium. A near-death experiencer, she has guided nearly 60,000 clients and appeared on The Drew Barrymore Show, Goop, Elle, and Gaia. Her books include the Hay House bestseller Infinite Life, Infinite Lessons and the forthcoming When Shadows Speak.",
  },
  {
    label: "Full Bio",
    text: "Susan Grau is an international bestselling author, inspirational speaker, grief and trauma expert, and intuitive medium. A near-death experiencer since the age of four, she has spent three decades guiding nearly 60,000 clients through loss, addiction, and transformation. Featured on The Drew Barrymore Show, Goop, Elle, The Hollywood Reporter, Gaia, and hundreds of stages and podcasts, her work has reached over 580 million people worldwide. She is the author of the Hay House international bestseller Infinite Life, Infinite Lessons and the forthcoming When Shadows Speak (2027), with a foreword by Valerie Bertinelli. Susan hosts the podcast Infinite Life, Infinite Wisdom, founded the My Infinite app, and is the subject of an upcoming documentary. She is represented by Meghan Amaral, CEO of M Media Corp.",
  },
];

function BioCard({ label, text, i }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <motion.div
      data-testid={`press-bio-${i}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: i * 0.1 }}
      className="flex flex-col border border-[#6B5D4F]/10 bg-[#FDFAF4] p-8"
    >
      <p className="font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">{label}</p>
      <p id={i === 1 ? "press-bio-50" : undefined} className="mt-5 flex-1 font-body text-sm font-light leading-relaxed text-[#6B5D4F]">
        {text}
      </p>
      <button
        type="button"
        data-testid={`press-bio-copy-${i}`}
        onClick={copy}
        className="mt-6 flex w-fit items-center gap-2 border border-[#C5A059]/50 px-5 py-2.5 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#FDFAF4]"
      >
        {copied ? <Check size={13} strokeWidth={1.5} /> : <Copy size={13} strokeWidth={1.5} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </motion.div>
  );
}

export default function PressContact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", inquiry_type: TYPES[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/press-inquiries`, form);
      toast.success("Inquiry received — Meghan's team will be in touch shortly.");
      setForm({ name: "", email: "", organization: "", inquiry_type: TYPES[0], message: "" });
    } catch {
      toast.error("Something went wrong — please email Meghan directly at meghan@mmediacorp.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="press" data-testid="press-section" className="relative overflow-hidden py-28 lg:py-40">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -left-8 bottom-0 select-none font-display text-[16rem] font-light italic leading-none text-[#C5A059]/10"
      >
        09
      </span>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-20 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
          >
            Chapter 09 — Media & Bookings
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
            className="mt-6 font-display text-4xl font-light leading-tight tracking-tighter text-[#6B5D4F] sm:text-5xl lg:text-6xl"
          >
            Book <span className="italic text-[#C5A059]">Susan</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 max-w-md font-body text-base font-light leading-relaxed text-[#8B7D6B]"
          >
            For television, podcast, keynote, and press opportunities, contact Susan's media team directly
            or send an inquiry through the form.
          </motion.p>

          <motion.img
            src="/images/headshot-pink.jpg"
            alt="Susan Grau"
            data-testid="press-headshot"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-12 aspect-square w-44 border border-[#C5A059]/50 object-cover shadow-[0_20px_40px_-15px_rgba(44,38,32,0.15)]"
          />
          <motion.div
            data-testid="press-contact-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-8 border border-[#C5A059]/50 bg-[#F7F1E6]/70 p-10"
          >
            <p className="font-script text-3xl text-[#C5A059]">contact susan directly</p>
            <div className="mt-6 space-y-4">
              <a
                data-testid="press-susan-email-link"
                href="mailto:susan@susangrau.com"
                className="gold-underline flex w-fit items-center gap-3 font-body text-sm font-light text-[#6B5D4F]"
              >
                <Mail size={15} strokeWidth={1.5} className="text-[#C5A059]" /> susan@susangrau.com
              </a>
              <a
                data-testid="press-direct-site-link"
                href="https://www.susangrau.com"
                target="_blank"
                rel="noopener noreferrer"
                className="gold-underline flex w-fit items-center gap-3 font-body text-sm font-light text-[#6B5D4F]"
              >
                <ArrowUpRight size={15} strokeWidth={1.5} className="text-[#C5A059]" /> susangrau.com
              </a>
            </div>

            <div className="my-8 h-px bg-[#C5A059]/40" aria-hidden="true" />

            <p className="font-script text-3xl text-[#C5A059]">media & bookings</p>
            <p className="mt-4 font-display text-3xl font-light text-[#6B5D4F]">{CONTACT.name}</p>
            <p className="mt-1 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">
              {CONTACT.role}
              <br />
              {CONTACT.role2}
            </p>
            <div className="mt-8 space-y-4">
              <a
                data-testid="press-phone-link"
                href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`}
                className="gold-underline flex w-fit items-center gap-3 font-body text-sm font-light text-[#6B5D4F]"
              >
                <Phone size={15} strokeWidth={1.5} className="text-[#C5A059]" /> {CONTACT.phone}
              </a>
              <a
                data-testid="press-email-link"
                href={`mailto:${CONTACT.email}`}
                className="gold-underline flex w-fit items-center gap-3 font-body text-sm font-light text-[#6B5D4F]"
              >
                <Mail size={15} strokeWidth={1.5} className="text-[#C5A059]" /> {CONTACT.email}
              </a>
            </div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-8 font-body text-sm font-light leading-relaxed text-[#8B7D6B]"
          >
            Every inquiry is delivered straight to Susan and Meghan's inboxes.
          </motion.p>
        </div>

        <motion.form
          data-testid="press-inquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
          className="border border-[#6B5D4F]/10 bg-[#F7F1E6]/60 p-8 lg:col-span-7 lg:p-14"
        >
          <p className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#8B7D6B]">Press Inquiry</p>
          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <label htmlFor="pi-name" className="font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">Name *</label>
              <input id="pi-name" data-testid="press-form-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
            </div>
            <div>
              <label htmlFor="pi-email" className="font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">Email *</label>
              <input id="pi-email" data-testid="press-form-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@outlet.com" className={inputCls} />
            </div>
            <div>
              <label htmlFor="pi-org" className="font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">Outlet / Organization</label>
              <input id="pi-org" data-testid="press-form-organization" value={form.organization} onChange={set("organization")} placeholder="Show, network, or publication" className={inputCls} />
            </div>
            <div>
              <label htmlFor="pi-type" className="font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">Inquiry Type</label>
              <select id="pi-type" data-testid="press-form-type" value={form.inquiry_type} onChange={set("inquiry_type")} className={`${inputCls} cursor-pointer`}>
                {TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="pi-message" className="font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]">Message *</label>
              <textarea id="pi-message" data-testid="press-form-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about the opportunity — dates, audience, format." className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            data-testid="press-form-submit"
            type="submit"
            disabled={sending}
            className="group relative mt-12 flex items-center gap-3 overflow-hidden bg-[#6B5D4F] px-10 py-4 font-body text-[0.7rem] font-medium uppercase tracking-[0.3em] text-[#FDFAF4] disabled:opacity-60"
          >
            <span className="absolute inset-0 -translate-x-full bg-[#C5A059] transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:translate-x-0" />
            <span className="relative">{sending ? "Sending..." : "Send Inquiry"}</span>
            <Send size={14} className="relative transition-transform duration-500 group-hover:translate-x-1" />
          </button>
        </motion.form>
      </div>

      <div data-testid="press-bios" className="mx-auto mt-24 max-w-7xl border-t border-[#6B5D4F]/10 px-6 pt-16 lg:px-10">
        <p className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]">
          Press-Ready Bios
        </p>
        <h3 className="mt-4 font-display text-3xl font-light tracking-tight text-[#6B5D4F] md:text-4xl">
          Approved copy, <span className="italic text-[#C5A059]">ready to paste</span>
        </h3>
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {BIOS.map((b, i) => (
            <BioCard key={b.label} label={b.label} text={b.text} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
